'use client';

import React, { useEffect } from 'react';
import { Button } from 'reactstrap';

import {
  CloseSvg, EmergencyJobIcon, GoBackIcon,
} from 'src/assets/images/job-details-slide-out/job-details-slide-out';

import CopyButton from 'src/components/job-slide-out/components/copy-button';
import useSlideOutFlagLabels from 'src/components/job-slide-out/components/use-slide-out-flag-labels';
import WatcherDropdown from 'src/components/job-slide-out/components/watcher-dropdown';

import { useAppSelector } from 'src/hooks/redux-hooks';

const jobStatus: Record<string, string> = {
  scheduling_in_progress: 'in-progress',
  scheduled: 'scheduled',
  work_in_progress: 'job-in-progress',
  paused: 'paused',
  work_completed: 'completed',
  canceled: 'canceled',
  closed: 'completed',
  created: 'requesting-awaitability',
  invoice_ready: 'job-in-progress',
  quality_check: 'paused',
  quoting: 'paused',
};

interface HeaderProps {
  closeJobDetailsSlideOut: () => void;
}

const Header: React.FC<HeaderProps> = ({ closeJobDetailsSlideOut }) => {
  const job = useAppSelector((state) => state?.jobDetails?.data);
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const isLoading = useAppSelector((state) => state?.jobDetails?.isLoading);
  // Mode-aware active-flag labels (SAAS-815 AC#5): the WO's own rollup in
  // jobDetails mode, the MR-wide roll-up in serviceRequestDetails mode — and
  // never a stale previous-WO rollup on the MR panel.
  const flagLabels = useSlideOutFlagLabels();

  useEffect(() => {
    const element = document.getElementById('side-panel-header');
    const element1 = document.getElementById('job-content-row');
    if (element && !isLoading) {
      const computedStyle = window.getComputedStyle(element);
      const slideEl = document
        ?.getElementsByClassName(
          'job-details-slide',
        )[0] as HTMLElement;
      slideEl?.style.setProperty(
        '--slide-out-header-height',
        `${computedStyle.height}`,
      );
      const computedStyle1 = window
        .getComputedStyle(element1 as HTMLElement);
      slideEl?.style.setProperty(
        '--job-content-row-height',
        `${computedStyle1.height}`,
      );
    }
  }, [isLoading]);

  const masterStatus = job?.master_status;
  const serviceCategory = job?.service_category;
  const primaryFlagLabel = flagLabels[0];

  return (
    <div id="side-panel-header" className="offcanvas-header">
      <div className="offcanvas-header-wrapper">
        <div className="job-title-outer">
          {isLoading
            ? <div className="common-loading w-36" />
            : (
              <div className="job-id">
                {type === 'jobDetails' ? `Job Detail (#${job?.job_no || ''})` : ''}
              </div>
            )}
          <div className="job-title-inner">
            {
              isLoading
                ? <div className="common-loading w-42" />
                : (
                  <div className="job-title">
                    {type === 'jobDetails'
                      ? (serviceCategory?.label as string)
                      : 'Maintenance Request'}
                  </div>
                )
}
            {/* TODO map with status */}
            {isLoading ? <div className="common-loading w-25" />

              : (
                <div className={`wo-tag ${
                  jobStatus?.[masterStatus?.slug] || ''
                }`}
                >
                  {(masterStatus?.label as string) || ''}
                </div>
              )}
            {primaryFlagLabel
              ? (
                <div className="flag-tag">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 7.5C2 7.5 2.5 7 4 7C5.5 7 6.5 8 8 8C9.5 8 10 7.5 10 7.5V1.5C10 1.5 9.5 2 8 2C6.5 2 5.5 1 4 1C2.5 1 2 1.5 2 1.5L2 11"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {primaryFlagLabel}
                </div>
              ) : null}
            {(job?.is_emergency_job || job?.has_go_back_option)
              ? (
                <React.Fragment>
                  {job?.has_go_back_option
                    ? (
                      <div className="go-back-tag">
                        <GoBackIcon />
                        Recall Job
                      </div>
                    ) : ''}
                  {job?.is_emergency_job
                    ? (
                      <div className="emergency-tag">
                        <EmergencyJobIcon />
                        Emergency
                      </div>
                    ) : ''}
                </React.Fragment>
              ) : ''}
          </div>
        </div>
        <div className="action-wrapper">
          <CopyButton />
          <WatcherDropdown />
          <Button
            type="button"
            color="close"
            onClick={closeJobDetailsSlideOut}
          >
            <CloseSvg />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Header;